export interface LenderDashboardLender {
  id: string;
  lender_name: string;
  created_at: string;
}

export interface LenderDashboardTransaction {
  lender_profile_id: string;
  transaction_type: string;
  amount: number | string;
  transaction_date: string;
}

export interface LenderDashboardData {
  lenders: LenderDashboardLender[];
  transactions: LenderDashboardTransaction[];
}

export interface DashboardStats {
  totalLenders: number;
  totalFundsReceived: number;
  totalFundsPaid: number;
  totalAccruedInterest: number;
  lenderStats: Array<{
    lender_name: string;
    principal: number;
    accrued_interest: number;
    next_payment_date: string;
    payment_schedule: string;
  }>;
}

const REPAYMENT_TYPES = ["interest_payment", "principal_repayment"];

export function calculateNextPaymentDate(startDate: string, schedule: string, today = new Date()): string {
  const start = new Date(startDate);

  let monthsToAdd = 0;
  switch (schedule) {
    case "monthly":
      monthsToAdd = 1;
      break;
    case "quarterly":
      monthsToAdd = 3;
      break;
    case "semi-annually":
      monthsToAdd = 6;
      break;
    case "annually":
      monthsToAdd = 12;
      break;
  }

  const nextDate = new Date(start);
  while (nextDate < today) {
    nextDate.setMonth(nextDate.getMonth() + monthsToAdd);
  }

  return nextDate.toISOString().split("T")[0];
}

export function computeDashboardStats(
  { lenders, transactions }: LenderDashboardData,
  today = new Date(),
): DashboardStats {
  const totalFundsReceived = transactions
    .filter((t) => t.transaction_type === "loan_received")
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalFundsPaid = transactions
    .filter((t) => REPAYMENT_TYPES.includes(t.transaction_type))
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const lenderStats = lenders.map((lender) => {
    const lenderTxns = transactions.filter((t) => t.lender_profile_id === lender.id);

    const principal = lenderTxns
      .filter((t) => t.transaction_type === "loan_received")
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const lastRepayment = lenderTxns
      .filter((t) => REPAYMENT_TYPES.includes(t.transaction_type))
      .sort((a, b) => new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime())[0];

    const startDate = lastRepayment ? new Date(lastRepayment.transaction_date) : new Date(lender.created_at);
    const daysDiff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    // The schema has no per-lender rate yet, so a flat 5% is assumed.
    const dailyRate = 0.05 / 365;
    const accrued_interest = principal * dailyRate * daysDiff;

    return {
      lender_name: lender.lender_name,
      principal,
      accrued_interest,
      next_payment_date: calculateNextPaymentDate(lender.created_at, "monthly", today),
      payment_schedule: "monthly",
    };
  });

  return {
    totalLenders: lenders.length,
    totalFundsReceived,
    totalFundsPaid,
    totalAccruedInterest: lenderStats.reduce((sum, l) => sum + l.accrued_interest, 0),
    lenderStats,
  };
}
